import React from "react";
import { Box, Flex, IconButton, Text } from "@chakra-ui/react";
import { CloseIcon } from "@chakra-ui/icons";
import { useFormContext } from "react-hook-form";
import { useInfiniteHits, useSearchBox } from "react-instantsearch";
import { AlgoliaSegmentObject } from "@common/interfaces/Algolia";
import { KYDMultiSelect } from "src/ui";
import { CreateCustomAudienceFormValues } from "./types";
import { KYD_AUDIENCE_DIALOG_STYLES } from "./kydAudienceDialogStyles";

const styles = KYD_AUDIENCE_DIALOG_STYLES;

function applySegmentSelection(
  form: ReturnType<typeof useFormContext<CreateCustomAudienceFormValues>>,
  selected: { label: string; value: string } | null,
  segmentHit: AlgoliaSegmentObject | undefined
) {
  form.setValue("segment", selected, {
    shouldDirty: true,
    shouldValidate: true,
  });
  form.setValue(
    "segment_size",
    segmentHit?.size != null ? segmentHit.size : undefined
  );
  if (selected) {
    form.setValue("genres", [], { shouldDirty: true });
    form.setValue("artists", [], { shouldDirty: true });
    if (!form.getValues("name")?.trim()) {
      form.setValue("name", selected.label?.trim() ?? "", {
        shouldDirty: true,
      });
    }
  } else {
    form.setValue("segment_size", undefined);
  }
}

const SegmentSearchField: React.FC = () => {
  const form = useFormContext<CreateCustomAudienceFormValues>();
  const { items: segments } = useInfiniteHits<AlgoliaSegmentObject>({
    escapeHTML: false,
    showPrevious: false,
  });
  const { refine } = useSearchBox();

  const selectedSegment = form.watch("segment");
  const segmentSize = form.watch("segment_size");

  const clearSegment = () => {
    applySegmentSelection(form, null, undefined);
  };

  return (
    <Box data-testid="kyd-audience-segment-select">
      {selectedSegment ? (
        <Flex
          align="center"
          justify="space-between"
          w="full"
          bg="white"
          border={styles.fieldBorder}
          borderRadius="8px"
          boxShadow={styles.fieldShadow}
          px="14px"
          py="17px"
          gap={2}
        >
          <Text
            flex={1}
            minW={0}
            fontFamily={styles.fontFamily}
            fontSize={styles.sectionLabelFontSize}
            fontWeight={500}
            color={styles.titleColor}
            lineHeight="19.2px"
            noOfLines={1}
            textTransform="uppercase"
          >
            {selectedSegment.label}
          </Text>
          <IconButton
            aria-label="Clear segment"
            icon={<CloseIcon boxSize={2.5} color="white" />}
            size="xs"
            minW="20px"
            h="20px"
            w="20px"
            borderRadius="full"
            bg={styles.segmentClearBg}
            onClick={clearSegment}
            _hover={{ bg: "gray.500" }}
          />
        </Flex>
      ) : (
        <KYDMultiSelect
          id="kyd-audience-segment"
          placeholder="Search or select a segment"
          label=""
          values={[]}
          options={segments.map((segment) => ({
            label: segment.name,
            value: segment.objectID,
          }))}
          onChange={(values) => {
            const selected = values[0] ?? null;
            const segmentHit = selected
              ? segments.find((s) => s.objectID === selected.value)
              : undefined;
            applySegmentSelection(form, selected, segmentHit);
          }}
          onInputChange={refine}
          maxAllowed={1}
        />
      )}
      {selectedSegment && segmentSize != null && (
        <Text fontSize="xs" color="gray.600" mt={1} fontFamily={styles.fontFamily}>
          ~{segmentSize} contacts
        </Text>
      )}
    </Box>
  );
};

export default SegmentSearchField;
